import { useState, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import './ImageUpload.css';

function ImageUpload({ images = [], onChange }) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    // Validation constants (matching backend requirements)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

    // Validate each file
    const validFiles = [];
    const errors = [];

    for (const file of files) {
      // Check file type
      if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
        const fileName = file.name.length > 30 ? file.name.substring(0, 30) + '...' : file.name;
        errors.push(`${fileName}: Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.`);
        continue;
      }

      // Check file extension (additional safety check)
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
        const fileName = file.name.length > 30 ? file.name.substring(0, 30) + '...' : file.name;
        errors.push(`${fileName}: Invalid file extension. Allowed: JPG, PNG, GIF, WebP.`);
        continue;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        const fileName = file.name.length > 30 ? file.name.substring(0, 30) + '...' : file.name;
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        errors.push(`${fileName}: File too large (${fileSizeMB}MB). Maximum size is 10MB.`);
        continue;
      }

      // Check if file is actually an image (not just mime type)
      const isValidImage = await validateImageFile(file);
      if (!isValidImage) {
        const fileName = file.name.length > 30 ? file.name.substring(0, 30) + '...' : file.name;
        errors.push(`${fileName}: File is corrupted or not a valid image.`);
        continue;
      }

      validFiles.push(file);
    }

    // Show errors if any
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error, { autoClose: 5000 }));
    }

    // If no valid files, return
    if (validFiles.length === 0) {
      return;
    }

    // Create preview URLs for valid images with metadata
    const newImages = await Promise.all(
      validFiles.map(async (file, index) => {
        // Get image dimensions
        const dimensions = await getImageDimensions(file);

        return {
          id: `temp-${Date.now()}-${index}`,
          file,
          url: URL.createObjectURL(file),
          isPrimary: images.length === 0 && index === 0,
          isNew: true,
          altText: '',
          size: file.size,
          width: dimensions.width,
          height: dimensions.height,
          format: file.type.split('/')[1].toUpperCase(),
        };
      })
    );

    // Show success message
    if (validFiles.length > 0) {
      toast.success(`${validFiles.length} image${validFiles.length > 1 ? 's' : ''} added successfully`);
    }

    onChange([...images, ...newImages]);
  };

  // Validate that file is actually a valid image by trying to load it
  const validateImageFile = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        // Check minimum dimensions (optional - prevent 1x1 pixel images)
        if (img.width < 10 || img.height < 10) {
          resolve(false);
        } else {
          resolve(true);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.src = url;

      // Timeout after 5 seconds
      setTimeout(() => {
        URL.revokeObjectURL(url);
        resolve(false);
      }, 5000);
    });
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleAltTextChange = (imageId, altText) => {
    const updatedImages = images.map(img =>
      img.id === imageId ? { ...img, altText } : img
    );
    onChange(updatedImages);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);

    // If we deleted the primary image and there are other images, make the first one primary
    if (updatedImages.length > 0 && !updatedImages.some(img => img.isPrimary)) {
      updatedImages[0].isPrimary = true;
    }

    onChange(updatedImages);
  };

  const handleSetPrimary = (imageId) => {
    const updatedImages = images.map(img => ({
      ...img,
      isPrimary: img.id === imageId,
    }));
    onChange(updatedImages);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(images);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onChange(items);
  };

  return (
    <div className="image-upload">
      {/* Drop Zone */}
      <div
        className={`image-dropzone ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
        </svg>
        <p>
          {isDragging ? 'Drop images here' : 'Drag and drop images here'}
        </p>
        <span>or click to browse</span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </div>

      {/* Image List */}
      {images.length > 0 && (
        <div className="image-list">
          <p className="image-list-hint">
            Drag to reorder • Click star to set as primary image
          </p>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="images">
              {(provided) => (
                <div
                  className="image-grid"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {images.map((image, index) => (
                    <Draggable key={image.id} draggableId={image.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          className={`image-item ${snapshot.isDragging ? 'dragging' : ''}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          {/* Drag Handle */}
                          <div className="drag-handle" {...provided.dragHandleProps} title="Drag to reorder">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                            </svg>
                          </div>

                          {/* Image Preview */}
                          <div className="image-preview">
                            <img src={image.url} alt={image.altText || `Product ${index + 1}`} />

                            {/* Primary Badge */}
                            {image.isPrimary && (
                              <div className="primary-badge">Primary</div>
                            )}

                            {/* Actions */}
                            <div className="image-actions">
                              <button
                                type="button"
                                className={`action-icon ${image.isPrimary ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetPrimary(image.id);
                                }}
                                title={image.isPrimary ? 'Primary image' : 'Set as primary'}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="action-icon delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(image.id);
                                }}
                                title="Delete"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Image Details */}
                          <div className="image-details">
                            {/* Alt Text Input */}
                            <input
                              type="text"
                              className="alt-text-input"
                              placeholder="Image description (for accessibility)..."
                              value={image.altText || ''}
                              onChange={(e) => handleAltTextChange(image.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />

                            {/* Image Metadata */}
                            {(image.size || image.width) && (
                              <div className="image-metadata">
                                {image.size && <span>{formatFileSize(image.size)}</span>}
                                {image.width && image.height && (
                                  <span>{image.width} × {image.height}</span>
                                )}
                                {image.format && <span>{image.format}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
    </div>
  );
}

export default ImageUpload;
